import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PaginatedResponse, Vacante, VacanteFiltros } from '../models/vacante.model';
import { VacanteGuardada } from '../models/vacante-guardada.model';
import { Postulacion } from '../models/postulacion.model';
import { banderaIso, PaisCatalogo, PAISES } from '../models/catalogos';
import { limpiarCiudadPrefijoPais } from '../utils/vacante-labels';

interface VacantesApiResponse {
  vacantes: Vacante[];
  total: number;
  page: number;
  pages: number;
}

export interface SugerenciaUbicacion {
  ciudad: string;
  ciudadSlug: string;
  distrito: string | null;
  distritoSlug: string;
  pais: string;
  etiqueta: string;
}

@Injectable({ providedIn: 'root' })
export class VacantesService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/public/vacantes`;

  buscar(filtros: VacanteFiltros): Observable<PaginatedResponse<Vacante>> {
    let params = new HttpParams();
    if (filtros.sector) params = params.set('category', filtros.sector);
    if (filtros.categoriaSlug) params = params.set('categorySlug', filtros.categoriaSlug);
    if (filtros.ciudad) params = params.set('city', filtros.ciudad);
    if (filtros.ciudadSlug) params = params.set('citySlug', filtros.ciudadSlug);
    if (filtros.pais) params = params.set('country', filtros.pais);
    if (filtros.paisSlug) params = params.set('countrySlug', filtros.paisSlug);
    if (filtros.modalidad) params = params.set('modality', filtros.modalidad);
    if (filtros.q) params = params.set('q', filtros.q);
    if (filtros.tipoContrato) params = params.set('tipoContrato', filtros.tipoContrato);
    if (filtros.seniority) params = params.set('seniority', filtros.seniority);
    if (filtros.salarioMin) params = params.set('salarioMin', filtros.salarioMin);
    if (filtros.salarioMax) params = params.set('salarioMax', filtros.salarioMax);
    if (filtros.sortBy) params = params.set('sortBy', filtros.sortBy);
    params = params.set('page', String((filtros.page ?? 0) + 1));
    params = params.set('limit', String(filtros.size ?? 12));

    // transferCache:false — /vacantes (bare, sin país en el path) es una de las
    // 6 rutas prerenderizadas en build; sin esto, la respuesta vacía capturada
    // en build quedaría congelada para siempre (el cliente la reusa via
    // TransferState en vez de refrescar al hidratar), mostrando "0 vacantes"
    // a usuarios reales indefinidamente.
    return this.http.get<VacantesApiResponse>(this.baseUrl, { params, transferCache: false }).pipe(
      map((res) => ({
        items: res.vacantes ?? [],
        page: (res.page ?? 1) - 1,
        size: filtros.size ?? 12,
        totalItems: res.total ?? 0,
        totalPages: res.pages ?? 0,
      })),
      catchError(() =>
        of({ items: [], page: 0, size: filtros.size ?? 12, totalItems: 0, totalPages: 0 })
      )
    );
  }

  /**
   * Autocomplete de ciudad/distrito — catálogo real, poblado solo al publicarse vacantes.
   * `pais` vacío = "Todos los países": se envía tal cual y el backend devuelve
   * sugerencias de todos los países disponibles, conservando el `pais` de cada
   * una (WEB-01).
   */
  ubicaciones(pais: string, q: string): Observable<SugerenciaUbicacion[]> {
    const params = new HttpParams().set('pais', pais ?? '').set('q', q ?? '');
    return this.http
      .get<{ ubicaciones: SugerenciaUbicacion[] }>(`${environment.apiBaseUrl}/public/ubicaciones`, { params })
      .pipe(
        map((res) => {
          const sugerencias = (res.ubicaciones ?? []).map((s) => ({
            ...s,
            // Filas heredadas del catálogo pueden traer la ciudad con el
            // prefijo de código de país del scraping ("mx Ciudad de México")
            // — se limpia la etiqueta/ciudad para que el dropdown nunca lo
            // muestre (WEB-02).
            ciudad: limpiarCiudadPrefijoPais(s.ciudad),
            etiqueta: limpiarCiudadPrefijoPais(s.etiqueta),
          }));
          // Defensa client-side (WEB-01): con un país específico se descarta
          // cualquier sugerencia cuyo `pais` no coincida con el solicitado.
          // Con "Todos los países" (pais vacío) NO se filtra por país.
          if (!pais) return sugerencias;
          const paisNorm = pais.trim().toUpperCase();
          return sugerencias.filter((s) => s.pais?.trim().toUpperCase() === paisNorm);
        }),
        catchError(() => of([]))
      );
  }

  obtenerPorSlug(slug: string): Observable<Vacante | null> {
    return this.http.get<Vacante>(`${this.baseUrl}/${slug}`).pipe(catchError(() => of(null)));
  }

  relacionadas(slug: string): Observable<Vacante[]> {
    return this.http.get<Vacante[]>(`${this.baseUrl}/${slug}/relacionadas`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Requiere sesión de candidato activa (token adjunto por authInterceptor) —
   * el backend valida el rol. Devuelve la lista completa de postulaciones del
   * candidato (misma identidad que el móvil: JWT del login OTP por teléfono),
   * usada por "Mis aplicaciones" y para derivar el estado "postulado" de una
   * vacante puntual.
   *
   * Los errores NO se tragan como lista vacía: una falla de comunicación no
   * debe fingir "el candidato no tiene postulaciones" (WEB-04).
   */
  postulaciones(): Observable<Postulacion[]> {
    return this.http
      .get<{ postulaciones: Postulacion[] }>(`${environment.apiBaseUrl}/candidato/postulaciones`)
      .pipe(map((res) => res.postulaciones ?? []));
  }

  /** Requiere sesión de candidato activa — el backend valida el rol. */
  guardarVacante(vacanteId: string): Observable<{ id: string; vacanteId: string }> {
    return this.http.post<{ id: string; vacanteId: string }>(
      `${environment.apiBaseUrl}/candidato/vacantes-guardadas/${vacanteId}`,
      {}
    );
  }

  /** Requiere sesión de candidato activa — el backend valida el rol. */
  eliminarVacanteGuardada(vacanteId: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.apiBaseUrl}/candidato/vacantes-guardadas/${vacanteId}`
    );
  }

  /**
   * Requiere sesión de candidato activa — el backend valida el rol.
   * Los errores NO se tragan como lista vacía: una falla de comunicación no
   * debe fingir "el candidato no tiene vacantes guardadas" (WEB-03).
   */
  vacantesGuardadas(): Observable<VacanteGuardada[]> {
    return this.http
      .get<{ vacantesGuardadas: VacanteGuardada[] }>(
        `${environment.apiBaseUrl}/candidato/vacantes-guardadas`
      )
      .pipe(map((res) => res.vacantesGuardadas ?? []));
  }

  obtenerPaises(): Observable<PaisCatalogo[]> {
    return this.http.get<any[]>(`${environment.apiBaseUrl}/public/paises`).pipe(
      map((res) => {
        if (!res || !Array.isArray(res)) return [];
        return res.flatMap((p) => {
          if (typeof p?.codigo !== 'string' || typeof p?.nombre !== 'string') return [];
          const match = PAISES.find((cat) => cat.code === p.codigo);
          const flag = banderaIso(p.codigo);
          if (!flag) return [];
          return [{
            code: p.codigo,
            nombre: p.nombre,
            dial: typeof p.dial === 'string' ? p.dial : match?.dial ?? '',
            slug: match?.slug || p.codigo.toLowerCase(),
            flag,
          }];
        });
      }),
      catchError(() => of([]))
    );
  }
}
